import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, InputBase, Button, CircularProgress, useTheme } from '@mui/material';
import { globalSearch, getOverviewStats } from '../services/api';
import { DISPLAY_FONT } from '../theme';

// ---------------------------------------------------------------------------
// Ledger redesign home. Three bands separated by heavy ink rules:
//   1. Hero — kicker + headline + intro, with a right-hand stats ledger.
//   2. Search — command-bar field ("> …" + accent Search button) + TRY links.
//   3. Browse — three ledger columns (People / Parliament / The record).
// Counts are live from /api/stats/overview; search is the existing global
// search rendered inline as ledger result rows.
// ---------------------------------------------------------------------------

const EXAMPLE_QUERIES = [
  'aged care',
  'National Reconstruction Fund',
  'Senator Penny Wong',
  'climate targets',
  'defence procurement',
];

// Hero stat rows: label + how to derive its value from the overview stats.
const HERO_STATS = [
  { label: 'Bills & acts', keys: ['bills'] },
  { label: 'Questions on Notice', keys: ['senateqon', 'houseqiw', 'questions'] },
  { label: 'Hansard records', keys: ['hansard'] },
  { label: 'Committee reports', keys: ['committeereports'] },
  { label: 'Parliamentarians', keys: ['parliamentarians'] },
];

// Three browse ledger columns (curated headline datasets, per the handoff).
const BROWSE_COLUMNS = [
  {
    index: '01',
    title: 'People',
    rows: [
      { label: 'Parliamentarians', key: 'parliamentarians', path: '/parliamentarians' },
      { label: 'Electorates', key: 'electorates', path: '/electorates' },
      { label: 'Parties', key: 'parties', path: '/parties' },
      { label: 'Ministries', key: 'ministries', path: '/ministries' },
    ],
  },
  {
    index: '02',
    title: 'Parliament',
    rows: [
      { label: 'Parliaments', key: 'parliaments', path: '/parliaments' },
      { label: 'Committees', key: 'committees', path: '/committees' },
      { label: 'Shadow ministries', key: 'shadowministries', path: '/shadowministries' },
      { label: 'Sitting calendars', key: 'calendar', path: '/calendar' },
    ],
  },
  {
    index: '03',
    title: 'The record',
    rows: [
      { label: 'Bills & acts', key: 'bills', path: '/bills' },
      { label: 'Questions on Notice', keys: ['senateqon', 'houseqiw', 'questions'], path: '/senateqon' },
      { label: 'Hansard', key: 'hansard', path: '/hansard' },
      { label: 'Committee reports', key: 'committeereports', path: '/committee-reports' },
    ],
  },
];

// Which global-search result buckets to surface, in order, with their titles.
const RESULT_TYPES = [
  { key: 'hansard', label: 'Hansard', path: '/hansard' },
  { key: 'senateqon', label: 'Senate QoN', path: '/senateqon' },
  { key: 'bills', label: 'Bills', path: '/bills' },
  { key: 'parliamentarians', label: 'Parliamentarians', path: '/parliamentarians' },
  { key: 'committees', label: 'Committees', path: '/committees' },
  { key: 'questions', label: 'Senate Estimates', path: '/questions' },
  { key: 'electorates', label: 'Electorates', path: '/electorates' },
  { key: 'parties', label: 'Parties', path: '/parties' },
  { key: 'tableddocuments', label: 'Tabled documents', path: '/tableddocuments' },
];

function sumKeys(stats, keys) {
  if (!stats) return null;
  return keys.reduce((acc, k) => acc + (stats[k] || 0), 0);
}

function fmtCount(n) {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  return n.toLocaleString();
}

function resultTitle(type, hit) {
  switch (type) {
    case 'hansard':
      return `${hit.speaker_name || 'Unknown speaker'} — ${hit.debate_title || ''}`;
    case 'parliamentarians':
      return hit.DisplayName || `${hit.GivenName || ''} ${hit.FamilyName || ''}`.trim();
    case 'committees':
      return hit.CommitteeName;
    case 'electorates':
      return `${hit.Electorate} (${hit.State})`;
    case 'parties':
      return hit.Name || hit.Abbreviation;
    case 'senateqon':
      return `Q${hit.question_no || ''}: ${hit.senator || 'Unknown'}`;
    case 'bills':
      return hit.title || 'Untitled bill';
    case 'questions':
      return `Q${hit.question_number || ''}: ${hit.senator || 'Unknown'}`;
    case 'tableddocuments':
      return hit.title || hit.document_type || 'Tabled document';
    default:
      return hit.title || hit.Name || 'Record';
  }
}

function resultMeta(type, hit) {
  switch (type) {
    case 'hansard':
      return [hit.chamber, hit.party, hit.date].filter(Boolean).join(' · ');
    case 'parliamentarians':
      return [hit.Party, hit.State].filter(Boolean).join(' · ');
    case 'committees':
      return [hit.CommitteeChamber, hit.CommitteeType].filter(Boolean).join(' · ');
    case 'senateqon':
      return [hit.portfolio, hit.status].filter(Boolean).join(' · ');
    case 'bills':
      return [hit.status, hit.originating_chamber, hit.portfolio].filter(Boolean).join(' · ');
    case 'questions':
      return hit.committee || 'Senate Estimates';
    case 'tableddocuments':
      return [hit.chamber, hit.date, hit.document_type].filter(Boolean).join(' · ');
    default:
      return '';
  }
}

export default function HomePage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const L = theme.palette.ledger;
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    getOverviewStats().then(setStats).catch(console.error);
  }, []);

  const runSearch = async (q) => {
    const term = (q ?? query).trim();
    if (!term) {
      setResults(null);
      return;
    }
    setLoading(true);
    try {
      const data = await globalSearch(term, 3);
      setResults({ ...data, query: term });
    } catch (e) {
      console.error('Search error:', e);
    }
    setLoading(false);
  };

  // Shared kicker/label style for browse + result rows (sans, sentence case).
  const kicker = { fontSize: '0.8125rem', fontWeight: 600, letterSpacing: 0 };

  return (
    <Box sx={{ animation: 'fadeUp .3s ease', '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1, transform: 'none' } } }}>

      {/* ============================ HERO ============================ */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0,7fr) minmax(0,4fr)' },
          gap: { xs: 4, md: 8 },
          alignItems: 'end',
          pt: { xs: 3, md: 5 },
          pb: { xs: 4, md: 6 },
          borderBottom: (theme) => `${theme.palette.ledger.ruleWidth} solid ${theme.palette.ledger.ruleColor}`,
        }}
      >
        <Box>
          <Typography sx={{ ...kicker, color: 'ledger.accentText', mb: 2.5 }}>
            Est. 1901 — indexed 2026
          </Typography>
          <Typography
            component="h1"
            sx={{
              fontFamily: DISPLAY_FONT,
              fontWeight: 600,
              letterSpacing: 0,
              lineHeight: 1.15,
              fontSize: { xs: '2rem', sm: '2.4rem', md: '2.75rem' },
              color: 'text.primary',
              textWrap: 'balance',
              mb: 2.5,
            }}
          >
            The parliamentary record, made searchable.
          </Typography>
          <Typography sx={{ fontSize: '1.05rem', lineHeight: 1.6, color: 'text.secondary', maxWidth: '52ch' }}>
            Every bill, speech, committee report and question on notice from the
            Parliament of Australia — one plain-language search across the lot.
          </Typography>
        </Box>

        {/* Stats ledger */}
        <Box sx={{ borderLeft: { xs: 'none', md: '1px solid' }, borderColor: { md: 'ledger.ruleColor' }, pl: { md: 4.5 } }}>
          {HERO_STATS.map((s) => (
            <Box
              key={s.label}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                py: 1.6,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: 'text.secondary' }}>
                {s.label}
              </Typography>
              <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary' }}>
                {fmtCount(sumKeys(stats, s.keys))}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* =========================== SEARCH =========================== */}
      <Box sx={{ py: { xs: 3, md: 4 }, borderBottom: (theme) => `${theme.palette.ledger.ruleWidth} solid ${theme.palette.ledger.ruleColor}` }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            border: (theme) => `${theme.palette.ledger.fieldWidth} solid ${theme.palette.ledger.ruleColor}`,
            borderRadius: 1.5,
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ fontWeight: 600, fontSize: '0.9rem', color: 'ledger.accentText', pl: 2.5, pr: 0.75 }}>
            {loading ? <CircularProgress size={15} sx={{ color: 'primary.main' }} /> : '>'}
          </Box>
          <InputBase
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
            placeholder="Search bills, members, questions, committees…"
            sx={{ fontSize: '1.05rem', py: 1.4, px: 1 }}
          />
          <Button
            variant="contained"
            onClick={() => runSearch()}
            sx={{ m: 0.6, px: 3.5, py: 1.3, fontWeight: 600 }}
          >
            Search
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mt: 1.75 }}>
          <Typography sx={{ ...kicker, color: 'text.secondary' }}>Try:</Typography>
          {EXAMPLE_QUERIES.map((ex) => (
            <Typography
              key={ex}
              onClick={() => { setQuery(ex); runSearch(ex); }}
              sx={{
                fontSize: '0.85rem',
                fontWeight: 600,
                color: 'text.secondary',
                cursor: 'pointer',
                textDecoration: 'underline',
                textDecorationColor: 'divider',
                textUnderlineOffset: '4px',
                '&:hover': { color: 'ledger.accentText', textDecorationColor: 'ledger.accentText' },
              }}
            >
              {ex}
            </Typography>
          ))}
        </Box>
      </Box>

      {/* ======================= SEARCH RESULTS ======================= */}
      {results && (
        <Box sx={{ py: { xs: 3, md: 4 }, borderBottom: (theme) => `${theme.palette.ledger.ruleWidth} solid ${theme.palette.ledger.ruleColor}` }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
            <Typography sx={{ fontSize: '0.95rem' }}>
              <Box component="span" sx={{ fontWeight: 700 }}>{(results.total || 0).toLocaleString()} results</Box>
              {' for '}
              <Box component="span" sx={{ fontWeight: 700, color: 'ledger.accentText' }}>&ldquo;{results.query}&rdquo;</Box>
            </Typography>
          </Box>

          {RESULT_TYPES.map((rt) => {
            const bucket = results.results?.[rt.key];
            if (!bucket || !bucket.total) return null;
            return (
              <Box key={rt.key}>
                {bucket.hits.slice(0, 3).map((hit, i) => (
                  <Box
                    key={hit.id || `${rt.key}-${i}`}
                    onClick={() => navigate(`${rt.path}?q=${encodeURIComponent(results.query)}`)}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '150px 1fr' },
                      gap: { xs: 0.5, sm: 3 },
                      py: 2.25,
                      px: 1,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      cursor: 'pointer',
                      transition: 'background-color .12s ease',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box sx={{ ...kicker, color: 'ledger.accentText', pt: 0.4 }}>
                      {rt.label}
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '1.05rem', fontWeight: 600, letterSpacing: 0, lineHeight: 1.25, mb: 0.5 }}>
                        {resultTitle(rt.key, hit)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'text.secondary' }}>
                        {resultMeta(rt.key, hit)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
                {bucket.total > 3 && (
                  <Box
                    onClick={() => navigate(`${rt.path}?q=${encodeURIComponent(results.query)}`)}
                    sx={{
                      ...kicker,
                      color: 'ledger.accentText',
                      py: 1,
                      px: 1,
                      cursor: 'pointer',
                      '&:hover': { color: 'ledger.accentText' },
                    }}
                  >
                    View all {bucket.total.toLocaleString()} {rt.label} →
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {/* =========================== BROWSE =========================== */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          borderBottom: (theme) => `${theme.palette.ledger.ruleWidth} solid ${theme.palette.ledger.ruleColor}`,
        }}
      >
        {BROWSE_COLUMNS.map((col, ci) => (
          <Box
            key={col.index}
            sx={{
              p: { xs: 3, md: 5 },
              pl: { md: ci === 0 ? 0 : 5 },
              borderLeft: { sm: ci === 0 ? 'none' : '1px solid' },
              borderColor: { sm: 'divider' },
              borderTop: { xs: ci === 0 ? 'none' : '1px solid', sm: 'none' },
            }}
          >
            <Typography sx={{ ...kicker, color: 'text.disabled', mb: 0.75 }}>
              {col.index}
            </Typography>
            <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: '1.375rem', fontWeight: 600, letterSpacing: 0, mb: 1.5 }}>
              {col.title}
            </Typography>
            {col.rows.map((row) => (
              <Box
                key={row.label}
                onClick={() => navigate(row.path)}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  py: 1.5,
                  px: 0.25,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  '&:hover .lbl': { color: 'ledger.accentText' },
                }}
              >
                <Typography className="lbl" sx={{ fontSize: '0.92rem', fontWeight: 600, color: 'text.primary', transition: 'color .12s ease' }}>
                  {row.label}
                </Typography>
                <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.9375rem', color: 'text.secondary' }}>
                  {fmtCount(sumKeys(stats, row.keys || [row.key]))}
                </Typography>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
